


import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown, Phone, Mail, MapPin, FileText, MessageCircle, Car, BookOpen, Calculator } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { FaWhatsapp } from 'react-icons/fa';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { translations } from '../data/translations';
import CatalogueModal from './CatalogueModal';
import { config } from '../config';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const modelsCategories = [
  {
    key: 'offRoad',
    subItems: [
      { key: 'bj30', name: 'BJ30 Hybrid', image: '/models/bj30', href: '/off-road/bj30-hybrid', details: 'Reliable Off-Road Experience', power: 180, torque: 320, wheelbase: 2500 },
      { key: 'bj40Honor', name: 'BJ40 HONOR', image: '/models/bj40-honor', href: '/off-road/bj40-honor', details: 'Special Edition with Extra Features', power: 210, torque: 400, wheelbase: 2800 },
      { key: 'bj40Pro', name: 'BJ40 PRO', image: '/models/bj40-pro', href: '/off-road/bj40-pro', details: 'Ultimate Off-Road SUV', power: 230, torque: 440, wheelbase: 2850 },
    ]
  },
  {
    key: 'sedan',
    subItems: [
      { key: 'u5Plus', name: 'U5 PLUS', image: '/models/u5-plus', href: '/sedan/u5-plus', details: 'Premium Sedan', power: 180, torque: 320, wheelbase: 2650 }
    ]
  }
];

// Dropdown items for other nav items
const navDropdownItems = {
  history: [
    { key: 'baicHistory', name: 'BAIC History', href: '/history/baic' },
    { key: 'offRoadHistory', name: 'Off-Road', href: '/history/off-road' },
  ],
  about: [
    { key: 'baicAbout', name: 'BAIC', href: '/about/overview' },
    { key: 'ggs', name: 'GGS', href: '/about/ggs' },
    { key: 'vision', name: 'Vision', href: '/about/vision' },
    { key: 'afterSales', name: 'After-Sales Service', href: '/about/after-sales-service' }
  ],
  connect: [
    { key: 'contactUs', name: 'Contact Us', href: '/connect/contact-us' },
    { key: 'careers', name: 'Careers', href: '/connect/careers' },
  ],
  showrooms_service: [
    { key: 'showroom', name: 'Showroom', href: '/show-rooms' },
    { key: 'serviceCenter', name: 'Service Center', href: '/service-centers' },
  ],
};

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language, toggleLanguage } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showModelsDropdown, setShowModelsDropdown] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [selectedModel, setSelectedModel] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null); // for other nav items

  const [isCatalogueOpen, setIsCatalogueOpen] = useState(false);
  const [showTopBar, setShowTopBar] = useState(true);

  const t = translations[language].nav;
  const tModels = translations[language].modelShowcase.models;
  const isRTL = language === 'ar';
  const isHomePage = location.pathname === '/';
  const isActive = (path) => location.pathname === path;

  useEffect(() => { document.documentElement.dir = isRTL ? 'rtl' : 'ltr'; }, [isRTL]);

  useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsScrolled(currentScrollY > 50);

      if (currentScrollY > lastScrollY && currentScrollY > 50) {
        setShowTopBar(false);
      } else {
        setShowTopBar(true);
      }

      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleModelsMouseEnter = () => { if (window.innerWidth >= 1024) { setShowModelsDropdown(true); setSelectedCategory('ALL'); } };
  const handleModelsMouseLeave = () => { if (window.innerWidth >= 1024) setShowModelsDropdown(false); };
  const handleCategoryClick = (categoryKey) => {
    if (selectedCategory === categoryKey) {
      setSelectedCategory(null);
      setSelectedModel(null);
      return;
    }
    setSelectedCategory(categoryKey);
    const firstModel = modelsCategories.find(c => c.key === categoryKey)?.subItems[0];
    setSelectedModel(firstModel || null);

  };
  const handleModelClick = (model) => setSelectedModel(model);
  const modelImage = (href) => `/assets/images/${href.toLowerCase()}.png`;

  const navItems = [
    { key: 'models', href: '/models' },
    { key: 'history', href: '/innovation' },
    { key: 'showrooms_service', href: '#' },
    { key: 'about', href: '/about' },
    ...(config.showGallery ? [{ key: 'gallery', href: '/gallery' }] : []),
    { key: 'connect', href: '/connect/contact-us' },
  ];

  const renderModelCard = (model) => (
    <Link key={model.name} to={model.href} className="group relative hover:scale-[1.02] transition-all duration-300" onClick={() => setShowModelsDropdown(false)}>
      <motion.div
        className="rounded-lg overflow-hidden"
        whileHover={{ scale: 1.05 }}
        transition={{ duration: 0.3 }}
      >
        <motion.img
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          src={modelImage(model.image)}
          alt={model.name}
          className="w-full object-contain"
        />
      </motion.div>
      <div className="mt-2 text-center text-[15px] font-normal text-gray-900 group-hover:text-red-600">{tModels[model.key] || model.name}</div>
    </Link>
  );

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white shadow-md"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >

      {/* Top Bar */}
      <div className={`bg-black text-white hidden lg:block transition-all duration-300 ease-in-out overflow-hidden ${showTopBar ? 'h-10 opacity-100' : 'h-0 opacity-0'}`}>
        <div className={`w-full px-4 sm:px-6 lg:px-8 h-full flex items-center gap-6 text-[10px] sm:text-[11px] font-medium tracking-wider uppercase ${isRTL ? 'flex-row-reverse justify-between' : 'justify-between'}`}>
          <div
            className={`flex-1 h-full ${isRTL ? 'text-right' : 'text-left'} ${isRTL ? "order-2" : "order-1"}`}
          >
          </div>

          <div className={`flex items-center gap-8 flex-shrink-0 ${isRTL ? "order-1" : "order-2"
            }`}>
            <Link to="/connect/contact-us" className="flex items-center gap-2 hover:text-red-500 transition-colors group">
              <span>{t.requestQuote}</span>
              <FileText size={16} strokeWidth={1.5} />
            </Link>

            <Link to="/test-drive" className="flex items-center gap-2 hover:text-red-500 transition-colors group">
              <span>{t.bookTestDrive}</span>
              <Car size={16} strokeWidth={1.5} />
            </Link>

            <button onClick={() => setIsCatalogueOpen(true)} className="flex items-center gap-2 hover:text-red-500 transition-colors group">
              <span>{t.requestCatalogue}</span>
              <BookOpen size={16} strokeWidth={1.5} />
            </button>

            <div className="h-4 w-px bg-gray-700"></div>

            <div className="flex items-center gap-6">
              <a href="https://wa.me/96879005800" target="_blank" rel="noopener noreferrer" className="hover:text-red-500 transition-colors" title="WhatsApp">
                <FaWhatsapp size={18} />
              </a>
              <a href="https://maps.app.goo.gl/pY4KSkaQiDbF66qZ8" target="_blank" rel="noopener noreferrer" className="hover:text-red-500 transition-colors" title="Find Us">
                <MapPin size={18} strokeWidth={1.5} />
              </a>
              <a href="tel:80050800" className="hover:text-red-500 transition-colors" title="Call Us">
                <Phone size={18} strokeWidth={1.5} />
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20 py-0">
          <Link to="/" className={`flex items-center gap-3`}>
            <img src="/assets/baic-logo.webp" alt="BAIC" className="h-10 w-auto object-contain" />
          </Link>

          <div className="hidden lg:flex items-center space-x-5 h-full gap-2">
            {navItems.map((item) => (
              <div key={item.key} className="relative h-full"
                onMouseEnter={() => { if (window.innerWidth >= 1024) { item.key === 'models' ? handleModelsMouseEnter() : setOpenDropdown(item.key); } }}
                onMouseLeave={() => { if (window.innerWidth >= 1024) { item.key === 'models' ? handleModelsMouseLeave() : setOpenDropdown(null); } }}
              >

                {item.key === 'models' ? (
                  <div
                    className={`text-sm h-full font-medium cursor-pointer flex items-center group transition-colors ${isActive('/models') || (selectedCategory && selectedCategory !== 'ALL')
                      ? 'text-red-600'
                      : 'text-gray-900 hover:text-red-600'
                      }`}
                  >
                    {t.models}
                    <ChevronDown className="ml-1 h-full w-4 transition-transform group-hover:rotate-180" />
                  </div>
                ) : (
                  navDropdownItems[item.key] ? (
                    <div
                      className={`text-sm h-full font-medium cursor-pointer flex items-center group transition-colors ${isActive(item.href)
                        ? 'text-red-600'
                        : 'text-gray-900 hover:text-red-600'
                        }`}
                    >
                      {t[item.key]}
                      <ChevronDown className="ml-1 h-4 w-4 transition-transform group-hover:rotate-180" />
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      className={`text-sm h-full font-medium flex items-center group transition-colors ${isActive(item.href)
                        ? 'text-red-600'
                        : 'text-gray-900 hover:text-red-600'
                        }`}
                    >
                      {t[item.key]}
                    </Link>
                  )
                )}

                {/* Desktop dropdown for other items - Professional White Theme */}
                {openDropdown === item.key && navDropdownItems[item.key] && (
                  <div className="relative" key={item.key}>
                    <div
                      className="cursor-pointer h-full flex items-center border-b-2 border-transparent hover:border-red-600 transition-all duration-300"
                      onMouseEnter={() => setOpenDropdown(item.key)}
                      onMouseLeave={() => setOpenDropdown(null)}
                    >
                      {item.name}
                    </div>

                    {openDropdown === item.key && navDropdownItems[item.key] && (
                      <div className={`mt-0 lg:block hidden fixed inset-x-0 w-full bg-white shadow-[0_20px_60px_rgba(0,0,0,0.08)] border-t border-gray-100 py-6 z-[100] animate-in fade-in slide-in-from-top-1 duration-200 ${showTopBar ? 'top-[120px]' : 'top-[80px]'}`} >
                        <div className="max-w-7xl mx-auto">
                          <div className="flex justify-center items-center gap-12">
                            {navDropdownItems[item.key].map(subItem => (
                              <Link
                                key={subItem.name}
                                to={subItem.href}
                                className="group relative px-4 py-2"
                                onClick={() => setOpenDropdown(null)}
                              >
                                <span className="relative z-10 text-base font-bold text-gray-700 group-hover:text-red-600 transition-colors duration-200">
                                  {t[subItem.key] || subItem.name}
                                </span>
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-red-600 transition-all duration-300 group-hover:w-full opacity-0 group-hover:opacity-100" />
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                  </div>

                )}

              </div>
            ))}

            <Button variant="outline" className="border-2 rounded-full px-6 uppercase tracking-wider font-bold transition-all duration-300 border-red-600 text-red-600 hover:bg-red-600 hover:text-white" onClick={toggleLanguage}>
              {language === 'en' ? 'AR' : 'EN'}
            </Button>
            <div className="h-8 w-px bg-gray-300 mx-4"></div>
            <img src="/assets/GGSLLC.gif" alt="AGA" className="h-12 w-auto object-contain" />
          </div>

          <div className="lg:hidden">
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 rounded-md transition-colors text-gray-900">
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Models Dropdown (Desktop) - Premium Mega Menu */}
      {showModelsDropdown && (
        <div className={`lg:block hidden fixed left-0 right-0 bg-white shadow-[0_20px_60px_rgba(0,0,0,0.08)] border-t border-gray-100 py-12 animate-in fade-in slide-in-from-top-1 duration-300 ${showTopBar ? 'top-[120px]' : 'top-[80px]'}`} onMouseEnter={handleModelsMouseEnter} onMouseLeave={handleModelsMouseLeave}>
          <div className="max-w-[1400px] mx-auto flex gap-16 items-start px-8">
            <div className="w-48 flex-shrink-0 space-y-2 border-r border-gray-100 pr-8 min-h-[400px]">
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-6 px-4">Categories</h4>
              {[{ key: 'ALL', label: t.all }, ...modelsCategories.map(c => ({ key: c.key, label: t[c.key] }))].map((category) => (
                <div key={category.key} onClick={() => handleCategoryClick(category.key)} className={`w-full cursor-pointer rounded-lg text-left px-4 py-3 transition-all duration-200 flex items-center justify-between group ${selectedCategory === category.key ? 'bg-black text-white shadow-lg scale-105' : 'text-gray-600 hover:bg-gray-50 hover:text-black'}`} style={{ fontWeight: 500, fontSize: '15px' }}>
                  {category.label}
                  {selectedCategory === category.key && <ChevronDown className="-rotate-90 w-4 h-4" />}
                </div>
              ))}
            </div>
            <div className="flex-1 min-w-0">
              {(selectedCategory === 'ALL' ? modelsCategories : modelsCategories.filter(c => c.key === selectedCategory)).map(cat => (
                <div key={cat.key} className="mb-12 last:mb-0 animate-in fade-in duration-500">
                  <h3 className="text-2xl font-bold text-gray-900 mb-8 border-b border-gray-100 pb-4">{selectedCategory === 'ALL' ? t[cat.key] : t[selectedCategory]}</h3>
                  {selectedCategory === 'ALL' ? (
                    <div className="grid grid-cols-5 gap-x-8 gap-y-12">{cat.subItems.map(model => renderModelCard(model))}</div>
                  ) : (
                    <div className="flex gap-16">
                      <div className="w-64 flex flex-col space-y-2">
                        {cat.subItems.map(model => (
                          <div key={model.name} onClick={() => handleModelClick(model)} className={`cursor-pointer p-3 rounded-xl transition-all flex items-center gap-4 group ${selectedModel?.name === model.name ? 'bg-gray-50 ring-1 ring-gray-200 shadow-sm' : 'hover:bg-white'}`}>
                            <div className="w-20 h-12 bg-gray-50 rounded-lg flex items-center justify-center p-1 mix-blend-multiply">
                              <motion.img
                                whileHover={{ scale: 1.1 }}
                                src={modelImage(model.image)}
                                alt={model.name}
                                className="w-full h-full object-contain"
                              />
                            </div>
                            <div className={`text-base font-medium transition-colors ${selectedModel?.name === model.name ? 'text-red-600' : 'text-gray-900 group-hover:text-red-500'}`}>{tModels[model.key] || model.name}</div>
                          </div>
                        ))}
                      </div>

                      {selectedModel && (
                        <div className="flex-1 flex gap-12 bg-gray-50 p-8 rounded-3xl animate-in fade-in slide-in-from-right-4 duration-500">
                          <div className="flex-1 flex items-center justify-center">
                            <motion.img
                              key={selectedModel.name}
                              initial={{ opacity: 0, x: 20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 0.5 }}
                              src={modelImage(selectedModel.image)}
                              alt={selectedModel.name}
                              className="w-full h-auto max-h-[300px] object-contain drop-shadow-2xl"
                            />
                          </div>
                          <div className="w-80 flex flex-col justify-center space-y-8">
                            <div>
                              <h2 className="text-4xl font-extrabold text-gray-900 mb-2 italic">{tModels[selectedModel.key] || selectedModel.name}</h2>
                              <p className="text-gray-500 leading-relaxed">{selectedModel.details}</p>
                            </div>
                            <div className="grid grid-cols-3 gap-4 border-t border-gray-200 pt-6">
                              <div>
                                <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1">Power</p>
                                <p className="text-xl font-bold text-gray-900">{selectedModel.power} <span className="text-xs font-normal text-gray-500">HP</span></p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1">Torque</p>
                                <p className="text-xl font-bold text-gray-900">{selectedModel.torque} <span className="text-xs font-normal text-gray-500">NM</span></p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1">W/Base</p>
                                <p className="text-xl font-bold text-gray-900">{selectedModel.wheelbase}</p>
                              </div>
                            </div>
                            <div className="flex gap-4 pt-2">
                              <Link to="/test-drive" className="flex-1" onClick={() => setShowModelsDropdown(false)}>
                                <button className="w-full bg-red-600 text-white py-3.5 px-6 rounded-full font-bold hover:bg-red-700 transition-all shadow-lg shadow-red-600/20 text-sm">
                                  Test Drive
                                </button>
                              </Link>
                              <Link to={selectedModel.href} className="flex-1" onClick={() => setShowModelsDropdown(false)}>
                                <button className="w-full border-2 border-gray-900 text-gray-900 py-3.5 px-6 rounded-full font-bold hover:bg-gray-900 hover:text-white transition-all text-sm">
                                  Details
                                </button>
                              </Link>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white shadow-lg border-t border-gray-200">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navItems.map(item => (
              <div key={item.key}>
                <div
                  className={`block px-3 py-3 rounded-xl font-bold text-base cursor-pointer flex items-center justify-between transition-all ${isActive(item.href) ? 'bg-red-600 text-white shadow-lg' : 'text-gray-900 hover:bg-gray-100 shadow-md'}`}
                  onClick={() => {
                    if (item.key === 'models') {
                      setShowModelsDropdown(!showModelsDropdown);
                      if (!showModelsDropdown) setSelectedCategory('ALL');
                    } else if (navDropdownItems[item.key]) {
                      setOpenDropdown(openDropdown === item.key ? null : item.key);
                    } else {
                      setIsMobileMenuOpen(false);
                      navigate(item.href);
                    }
                  }}
                >
                  {t[item.key]}
                  {item.key === 'models' || navDropdownItems[item.key] ? <ChevronDown className={`h-5 w-5 transition-transform ${(item.key === 'models' ? showModelsDropdown : openDropdown === item.key) ? 'rotate-180' : ''}`} /> : null}
                </div>

                {/* Mobile dropdown */}
                {(item.key === 'models' ? showModelsDropdown : openDropdown === item.key) && (
                  <div className="pl-6 mt-2 space-y-2">
                    {item.key === 'models'
                      ? [{ key: 'ALL', label: t.all }, ...modelsCategories.map(c => ({ key: c.key, label: t[c.key] }))].map(category => (
                        <div key={category.key}>
                          <div className={`p-4 rounded-xl font-bold cursor-pointer transition-all flex items-center justify-between ${selectedCategory === category.key ? 'bg-red-600 text-white shadow-xl' : 'bg-white shadow-lg hover:shadow-xl'}`} onClick={() => handleCategoryClick(category.key)}>
                            {category.label}
                            <ChevronDown className={`h-5 w-5 transition-transform ${selectedCategory === category.key ? 'rotate-180' : ''}`} />
                          </div>
                          {selectedCategory === category.key && (
                            <div className="grid grid-cols-2 gap-3 mt-4 pl-4">
                              {(category.key === 'ALL'
                                ? modelsCategories.flatMap(c => c.subItems)
                                : modelsCategories.find(c => c.key === category.key)?.subItems || []
                              ).map(model => (
                                <Link key={model.name} to={model.href} className="p-3 bg-white rounded-xl shadow-md hover:shadow-lg hover:bg-red-50 font-semibold text-sm" onClick={() => setIsMobileMenuOpen(false)}>
                                  {tModels[model.key] || model.name}
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      ))
                      : navDropdownItems[item.key]?.map(subItem => (
                        <Link key={subItem.name} to={subItem.href} className="block px-3 py-2 rounded-lg text-gray-900 hover:bg-gray-100" onClick={() => setIsMobileMenuOpen(false)}>
                          {t[subItem.key] || subItem.name}
                        </Link>
                      ))
                    }
                  </div>
                )}
              </div>
            ))}


            {/* Mobile Language Switcher */}
            <div className="mt-4 pt-4 border-t border-gray-100 px-1">
              <button
                onClick={() => {
                  toggleLanguage();
                  setIsMobileMenuOpen(false);
                }}
                className="w-full px-3 py-3 rounded-xl font-bold text-base cursor-pointer flex items-center justify-center transition-all bg-gray-50 text-gray-900 hover:bg-gray-100 hover:text-red-600 space-x-2"
              >
                <span>{language === 'en' ? 'Arabic' : 'English'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <CatalogueModal
        isOpen={isCatalogueOpen}
        onClose={() => setIsCatalogueOpen(false)}
        modelName="General"
      />

    </nav>
  );
};

export default Navbar;

